/**
 * Enhanced error messages with actionable feedback
 * Provides user-friendly error messages with suggestions
 */

export class ErrorMessageBuilder {
  constructor() {
    this.errorTemplates = this.initializeTemplates();
  }

  /**
   * Build user-friendly error message
   */
  buildError(error, context = {}) {
    const errorType = this.detectErrorType(error);
    const template = this.errorTemplates[errorType] || this.errorTemplates.default;

    return {
      message: this.formatMessage(template.message, error, context),
      suggestion: this.formatMessage(template.suggestion, error, context),
      severity: template.severity,
      code: errorType,
      actionable: template.actionable || false,
      actions: template.actions ? template.actions.map(a => this.formatMessage(a, error, context)) : []
    };
  }

  /**
   * Detect error type from error object
   */
  detectErrorType(error) {
    if (!error) return 'unknown';

    const message = (error.message || '').toLowerCase();
    const stack = (error.stack || '').toLowerCase();

    // Network errors
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return 'network';
    }

    // Timeout errors
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'timeout';
    }

    // Validation errors
    if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
      return 'validation';
    }

    // Rate limit errors
    if (message.includes('rate limit') || message.includes('429')) {
      return 'rate_limit';
    }

    // Authentication errors
    if (message.includes('auth') || message.includes('unauthorized') || message.includes('401')) {
      return 'authentication';
    }

    // Node execution errors
    if (message.includes('node') && (message.includes('failed') || message.includes('error'))) {
      return 'node_execution';
    }

    // Flow structure errors
    if (message.includes('cycle') || message.includes('circular') || message.includes('dependency')) {
      return 'flow_structure';
    }

    // Variable errors
    if (message.includes('variable') || message.includes('undefined')) {
      return 'variable';
    }

    // Condition errors
    if (message.includes('condition') || message.includes('expression')) {
      return 'condition';
    }

    // API errors
    if (message.includes('api') || message.includes('http') || message.includes('500') || message.includes('404')) {
      return 'api';
    }

    return 'unknown';
  }

  /**
   * Format message with error and context
   */
  formatMessage(template, error, context) {
    if (typeof template === 'function') {
      return template(error, context);
    }

    let message = template;

    // Build final error message with all context
    message = message.replace(/\{error\}/g, error?.message || 'Unknown error');
    message = message.replace(/\{nodeId\}/g, context.nodeId || 'unknown');
    message = message.replace(/\{nodeType\}/g, context.nodeType || 'unknown');
    message = message.replace(/\{variable\}/g, context.variable || 'unknown');
    message = message.replace(/\{url\}/g, context.url || 'unknown');
    message = message.replace(/\{retryAfter\}/g, context.retryAfter || '60');

    return message;
  }

  /**
   * Initialize error message templates
   */
  initializeTemplates() {
    return {
      network: {
        message: 'Network connection failed. Please check your internet connection.',
        suggestion: 'Check your internet connection and try again. If the problem persists, the server may be temporarily unavailable.',
        severity: 'high',
        actionable: true,
        actions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a few moments and try again'
        ]
      },

      timeout: {
        message: 'Operation timed out. The flow execution took too long.',
        suggestion: 'Your flow may be too complex or contain an infinite loop. Try simplifying the flow or adding timeout limits.',
        severity: 'high',
        actionable: true,
        actions: [
          'Simplify your flow',
          'Check for infinite loops',
          'Add timeout limits to long-running nodes',
          'Break large flows into smaller sub-flows'
        ]
      },

      validation: {
        message: 'Flow validation failed: {error}',
        suggestion: 'Please fix the validation errors before executing the flow.',
        severity: 'medium',
        actionable: true,
        actions: [
          'Review the validation errors above',
          'Fix missing required fields',
          'Check node configurations',
          'Ensure all edges connect valid nodes'
        ]
      },

      rate_limit: {
        message: 'Rate limit exceeded. Too many requests.',
        suggestion: 'Please wait {retryAfter} seconds before trying again.',
        severity: 'medium',
        actionable: true,
        actions: [
          'Wait before trying again',
          'Reduce the frequency of executions',
          'Contact support if you need higher limits'
        ]
      },

      authentication: {
        message: 'Authentication failed. Please log in again.',
        suggestion: 'Your session may have expired. Please log in to continue.',
        severity: 'high',
        actionable: true,
        actions: [
          'Log in again',
          'Check your credentials',
          'Clear browser cache and cookies'
        ]
      },

      node_execution: {
        message: 'Node execution failed: {error}',
        suggestion: (error, context) => {
          const nodeType = context.nodeType || 'unknown';
          return `The ${nodeType} node failed to execute. Check the node configuration and try again.`;
        },
        severity: 'high',
        actionable: true,
        actions: [
          'Check the node configuration',
          'Verify all required fields are filled',
          'Check node dependencies',
          'Review error logs for details'
        ]
      },

      flow_structure: {
        message: 'Flow structure error: {error}',
        suggestion: 'Your flow contains structural issues (cycles, missing connections, etc.). Please fix these before executing.',
        severity: 'high',
        actionable: true,
        actions: [
          'Check for circular dependencies',
          'Ensure all nodes are connected',
          'Remove unreachable nodes',
          'Use the flow validator to find issues'
        ]
      },

      variable: {
        message: 'Variable error: {error}',
        suggestion: (error, context) => {
          const variable = context.variable || 'unknown';
          return `The variable "${variable}" is not defined or cannot be accessed. Check variable names and scope.`;
        },
        severity: 'medium',
        actionable: true,
        actions: [
          'Check variable names for typos',
          'Ensure variables are set before use',
          'Check variable scope (global vs local)',
          'Use setVariable nodes to define variables'
        ]
      },

      condition: {
        message: 'Condition evaluation failed: {error}',
        suggestion: 'The condition expression is invalid or cannot be evaluated. Check the syntax and variable references.',
        severity: 'medium',
        actionable: true,
        actions: [
          'Check condition syntax',
          'Verify variable names are correct',
          'Use simple comparisons (>, <, ==, !=)',
          'Test condition with sample values'
        ]
      },

      api: {
        message: 'API call failed: {error}',
        suggestion: (error, context) => {
          const url = context.url || 'unknown';
          return `The API call to "${url}" failed. Check the URL, authentication, and network connectivity.`;
        },
        severity: 'high',
        actionable: true,
        actions: [
          'Verify the API URL is correct',
          'Check API authentication credentials',
          'Test the API endpoint manually',
          'Review API response format'
        ]
      },

      unknown: {
        message: 'An unexpected error occurred: {error}',
        suggestion: 'Please try again. If the problem persists, contact support with the error details.',
        severity: 'medium',
        actionable: false,
        actions: [
          'Try again',
          'Refresh the page',
          'Contact support if issue persists'
        ]
      },

      default: {
        message: 'Error: {error}',
        suggestion: 'Please review the error and try again.',
        severity: 'medium',
        actionable: false,
        actions: []
      }
    };
  }

  /**
   * Build error message for node execution
   */
  buildNodeError(node, error, context = {}) {
    return this.buildError(error, {
      ...context,
      nodeId: node?.id,
      nodeType: node?.type
    });
  }

  /**
   * Build error message for flow validation
   */
  buildValidationError(errors, warnings = []) {
    return {
      message: `Flow validation failed with ${errors.length} error(s) and ${warnings.length} warning(s)`,
      suggestion: 'Please fix the errors before executing the flow.',
      severity: 'high',
      actionable: true,
      errors: errors,
      warnings: warnings,
      actions: [
        'Review all validation errors',
        'Fix missing required fields',
        'Check node configurations',
        'Resolve structural issues'
      ]
    };
  }

  /**
   * Build error message for timeout
   */
  buildTimeoutError(duration, maxDuration) {
    return {
      message: `Flow execution timed out after ${Math.round(duration / 1000)}s`,
      suggestion: `The flow exceeded the maximum execution time of ${Math.round(maxDuration / 1000)}s. Consider simplifying the flow or breaking it into smaller parts.`,
      severity: 'high',
      actionable: true,
      duration: duration,
      maxDuration: maxDuration,
      actions: [
        'Simplify the flow',
        'Break into smaller sub-flows',
        'Add timeout limits to long-running nodes',
        'Check for infinite loops'
      ]
    };
  }
}

// Global instance
export const errorMessageBuilder = new ErrorMessageBuilder();

// Convenience functions
export function buildError(error, context) {
  return errorMessageBuilder.buildError(error, context);
}

export function buildNodeError(node, error, context) {
  return errorMessageBuilder.buildNodeError(node, error, context);
}

export function buildValidationError(errors, warnings) {
  return errorMessageBuilder.buildValidationError(errors, warnings);
}

export function buildTimeoutError(duration, maxDuration) {
  return errorMessageBuilder.buildTimeoutError(duration, maxDuration);
}

export default ErrorMessageBuilder;

